// =============================================================================
// APP LAYOUT - Shared layout with sidebar for all authenticated pages
// =============================================================================

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../stores/authStore.js';
import { useChatStore } from '../../stores/chatStore.js';
import { NotificationBell } from '../notifications/index.js';
import {
  MessageCircle,
  Phone,
  Users,
  Camera,
  CalendarDays,
  Settings,
  LogOut,
  ChevronsRight,
} from '../ui/Icons.js';
import { IconButton } from '../ui/Button.js';

interface AppLayoutProps {
  children: React.ReactNode;
  /** Page title shown in mobile header */
  title?: string;
  /** Hide the desktop header (useful when page has its own header) */
  hideDesktopHeader?: boolean;
}

// Primary nav items shown on both desktop sidebar and mobile bottom nav
const navItems = [
  { path: '/chats', icon: MessageCircle, labelKey: 'nav.chats' },
  { path: '/calls', icon: Phone, labelKey: 'nav.calls' },
  { path: '/photos', icon: Camera, labelKey: 'nav.photos' },
  { path: '/family', icon: Users, labelKey: 'nav.family' },
  { path: '/calendar', icon: CalendarDays, labelKey: 'nav.calendar' },
] as const;

// Settings always sits at the very bottom of the sidebar and end of mobile bar
const settingsItem = { path: '/settings', icon: Settings, labelKey: 'nav.settings' };

// Persisted sidebar layout preference (per-device). Replaces the old
// hover-to-expand: the user pins the rail open with the chevron and chooses
// "only icons" or "only labels", and the choice survives reloads. A chrome
// layout preference is per-device by nature, so localStorage is the right home.
const LS_EXPANDED = 'chlann.sidebar.expanded';
const LS_CONTENT = 'chlann.sidebar.content'; // 'icons' | 'labels'

function readExpanded(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(LS_EXPANDED) === 'true';
  } catch {
    return false;
  }
}

function readContent(): 'icons' | 'labels' {
  if (typeof window === 'undefined') return 'labels';
  try {
    return window.localStorage.getItem(LS_CONTENT) === 'icons' ? 'icons' : 'labels';
  } catch {
    return 'labels';
  }
}

export function AppLayout({ children, title, hideDesktopHeader = false }: AppLayoutProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { conversations } = useChatStore();
  const location = useLocation();
  const navigate = useNavigate();

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const activeConversationId = useChatStore((s) => s.activeConversationId);

  // Sidebar layout preference. `expanded` controls width (pinned-open rail vs a
  // compact icon column); `content` chooses icons-only or labels-only when open.
  const [expanded, setExpanded] = useState<boolean>(readExpanded);
  const [content, setContent] = useState<'icons' | 'labels'>(readContent);

  const toggleExpanded = () => {
    setExpanded((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(LS_EXPANDED, String(next));
      } catch {
        /* storage disabled: a session-only preference is acceptable */
      }
      return next;
    });
  };

  const chooseContent = (c: 'icons' | 'labels') => {
    setContent(c);
    try {
      window.localStorage.setItem(LS_CONTENT, c);
    } catch {
      /* storage disabled: a session-only preference is acceptable */
    }
  };

  // A collapsed rail is always icons. When pinned open the user sees exactly one
  // of "only icons" or "only labels" (never both), matching the requested toggle.
  const showLabel = expanded && content === 'labels';
  const showIcon = !expanded || content === 'icons';

  const currentPath = location.pathname;
  // True when the user is reading or typing in a specific chat. The chat
  // view renders its own header in that case, so the AppLayout mobile
  // header is suppressed to avoid the double-stacked-bars layout.
  const inActiveConversation = currentPath.startsWith('/chats') && !!activeConversationId;

  const isActive = (path: string) => {
    if (path === '/chats') {
      return currentPath === '/chats' || currentPath.startsWith('/chats/');
    }
    return currentPath.startsWith(path);
  };

  return (
    <div className="flex h-screen">
      {/* Skip to content link for keyboard/screen reader accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:p-4 focus:bg-primary focus:text-primary-on focus:rounded-lg focus:outline-none"
      >
        Skip to content
      </a>

      {/* Sidebar navigation (tablet/desktop) */}
      <aside
        className={`
          hidden md:flex flex-col border-r border-line-2 bg-surface-2 flex-shrink-0
          transition-all duration-300 ease-in-out
          ${expanded ? 'w-56' : 'w-16'}
        `}
      >
        {/* Logo - clickable to go home */}
        <button
          type="button"
          onClick={() => navigate('/chats')}
          className="p-4 border-b border-line-2 text-left hover:bg-surface-3 transition-colors flex items-center gap-3 min-h-[60px]"
        >
          <img
            src="/icons/cork-badge-clean.webp"
            alt="Chlann"
            className="w-8 h-8 flex-shrink-0 rounded-full"
            draggable={false}
          />
          <span
            className={`
              text-sm font-semibold text-primary whitespace-nowrap
              transition-all duration-300 ease-in-out overflow-hidden
              ${showLabel ? 'opacity-100 max-w-[140px]' : 'opacity-0 max-w-0'}
            `}
          >
            Do Theaghlach
          </span>
        </button>

        {/* Sidebar controls: pin the rail open/closed, and - when open - toggle
            between only icons and only labels. Replaces the old hover-to-expand
            so the layout is a deliberate, persisted choice, not a transient
            hover state that shifts the whole page when the cursor drifts over. */}
        <div className={`flex items-center gap-1 px-2 mt-2 ${expanded ? 'justify-between' : 'justify-center'}`}>
          <button
            type="button"
            onClick={toggleExpanded}
            className="flex items-center justify-center h-9 w-9 rounded-lg bg-surface-3 hover:bg-surface-sunken transition-colors text-ink-2"
            title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-pressed={expanded}
          >
            <ChevronsRight
              className={`w-5 h-5 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
          {expanded && (
            <button
              type="button"
              onClick={() => { chooseContent(content === 'icons' ? 'labels' : 'icons'); }}
              className="flex items-center justify-center h-9 w-9 rounded-lg bg-surface-3 hover:bg-surface-sunken transition-colors text-ink-2"
              title={content === 'icons' ? 'Show labels' : 'Show icons only'}
              aria-label={content === 'icons' ? 'Show labels' : 'Show icons only'}
            >
              {content === 'icons' ? (
                // text-lines glyph: tapping switches the rail to labels
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M4 7h16M4 12h16M4 17h10" />
                </svg>
              ) : (
                // dots glyph: tapping switches the rail to icons only
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                  <circle cx="7" cy="7" r="2" />
                  <circle cx="7" cy="17" r="2" />
                  <circle cx="17" cy="7" r="2" />
                  <circle cx="17" cy="17" r="2" />
                </svg>
              )}
            </button>
          )}
        </div>

        {/* Nav items: primary items → Dictionary (desktop-only) → Settings */}
        <nav className="flex-1 py-4">
          {navItems.map(({ path, icon: Icon, labelKey }) => (
            <SidebarNavButton
              key={path}
              icon={<Icon className="w-5 h-5" />}
              label={t(labelKey)}
              to={path}
              active={isActive(path)}
              showIcon={showIcon}
              showLabel={showLabel}
              badge={path === '/chats' ? totalUnread : undefined}
            />
          ))}
          {/* Dictionary — desktop sidebar only, before Settings */}
          <SidebarNavButton
            icon={<span className="w-5 h-5 flex items-center justify-center text-base font-semibold leading-none">á</span>}
            label={t('nav.dictionary', 'Foclóir')}
            to="/dictionary"
            active={isActive('/dictionary')}
            showIcon={showIcon}
            showLabel={showLabel}
          />
          <SidebarNavButton
            icon={<settingsItem.icon className="w-5 h-5" />}
            label={t(settingsItem.labelKey)}
            to={settingsItem.path}
            active={isActive(settingsItem.path)}
            showIcon={showIcon}
            showLabel={showLabel}
          />
        </nav>

        {/* User info & logout */}
        <div className="p-3 border-t border-line-2">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary text-primary-on flex items-center justify-center text-sm font-semibold flex-shrink-0">
              {user?.displayName?.charAt(0).toUpperCase() || '?'}
            </div>
            <span
              className={`
                text-sm text-ink-2 truncate whitespace-nowrap
                transition-all duration-300 ease-in-out overflow-hidden
                ${showLabel ? 'opacity-100 w-auto' : 'opacity-0 w-0'}
              `}
            >
              {user?.displayName}
            </span>
          </div>
          <button
            type="button"
            onClick={() => logout()}
            className={`
              chrome-target w-full rounded-lg bg-surface-3 text-sm hover:bg-surface-sunken transition-all duration-300
              flex items-center justify-center gap-2 min-h-[44px]
              ${showLabel ? 'px-3 py-2' : 'p-2'}
            `}
            title={t('auth.logout')}
            aria-label={t('auth.logout')}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            <span
              className={`
                whitespace-nowrap transition-all duration-300 ease-in-out overflow-hidden
                ${showLabel ? 'opacity-100 w-auto' : 'opacity-0 w-0'}
              `}
            >
              {t('auth.logout')}
            </span>
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header (mobile only). Hidden inside an active conversation
            because the chat view renders its own header (back, peer name,
            search, menu) - stacking both wastes ~56px of vertical space.
            Previously also showed a hardcoded "Do Theaghlach" string that
            could read out as the user's family even when they weren't in
            one yet (state desync with /family page). Now we just show the
            page title (or brand fallback). Family identity, when it
            matters, lives on the Family page itself. */}
        {!inActiveConversation && (
          <header className="flex items-center justify-between border-b border-line-2 px-4 py-2 flex-shrink-0 md:hidden">
            <button
              type="button"
              onClick={() => navigate('/chats')}
              className="hover:opacity-80 transition-opacity flex items-center gap-2 min-w-0"
            >
              <img
                src="/icons/cork-badge-clean.webp"
                alt="Chlann"
                className="w-7 h-7 rounded-full flex-shrink-0"
                draggable={false}
              />
              <span className="text-lg font-semibold text-primary truncate">
                {title ?? 'Chlann'}
              </span>
            </button>
            <div className="flex items-center gap-3 flex-shrink-0">
              <NotificationBell />
              <IconButton
                icon={<LogOut className="w-4 h-4" />}
                label={t('auth.logout')}
                onClick={() => logout()}
                className="chrome-target"
              />
            </div>
          </header>
        )}

        {/* Desktop header with notifications */}
        {!hideDesktopHeader && (
          <header className="hidden md:flex items-center justify-end border-b border-line-2 px-4 py-2 flex-shrink-0">
            <NotificationBell />
          </header>
        )}

        {/* Main content */}
        <main id="main-content" className="flex-1 overflow-hidden">
          {children}
        </main>

        {/* Bottom navigation (mobile) — primary items + Settings */}
        <nav className="border-t border-line-2 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] flex-shrink-0 md:hidden">
          <div className="flex justify-around">
            {navItems.map(({ path, icon: Icon, labelKey }) => (
              <NavButton
                key={path}
                icon={<Icon className="w-5 h-5" />}
                label={t(labelKey)}
                to={path}
                active={isActive(path)}
                badge={path === '/chats' ? totalUnread : undefined}
              />
            ))}
            <NavButton
              icon={<settingsItem.icon className="w-5 h-5" />}
              label={t(settingsItem.labelKey)}
              to={settingsItem.path}
              active={isActive(settingsItem.path)}
            />
          </div>
        </nav>
      </div>
    </div>
  );
}

// =============================================================================
// NAV BUTTONS
// =============================================================================

function NavButton({
  icon,
  label,
  to,
  active = false,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
  active?: boolean;
  badge?: number;
}) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className={`chrome-target relative flex flex-col items-center p-2 min-w-[44px] min-h-[44px] rounded-lg transition-colors ${
        active ? 'text-primary bg-primary/10' : 'text-ink-3 hover:text-ink-2 hover:bg-surface-3'
      }`}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
    >
      {active && (
        <span className="absolute top-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-primary" />
      )}
      <span className="relative">
        {icon}
        {badge != null && badge > 0 && (
          // Small dot indicator on the bottom nav. The exact count lives on
          // the conversation row's avatar; a number on the icon here was
          // visually colliding with the speech-bubble icon's left curve and
          // reading as "0" + badge text (e.g. "015" instead of "15").
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-secondary rounded-full ring-2 ring-surface-1 animate-badge-pulse" aria-label={`${badge} unread`} />
        )}
      </span>
      <span className="mt-1 text-xs">{label}</span>
    </button>
  );
}

function SidebarNavButton({
  icon,
  label,
  to,
  active = false,
  showIcon = true,
  showLabel = false,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
  active?: boolean;
  showIcon?: boolean;
  showLabel?: boolean;
  badge?: number;
}) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className={`chrome-target relative w-full flex items-center gap-3 px-4 py-3 transition-colors ${
        active
          ? 'text-primary bg-primary/10'
          : 'text-ink-2 hover:text-ink-1 hover:bg-surface-3'
      }`}
      title={label}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
    >
      {active && (
        <motion.div
          layoutId="sidebar-indicator"
          className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-primary"
          transition={{ type: 'spring', stiffness: 500, damping: 35 }}
        />
      )}
      {showIcon && (
        <span className="relative flex-shrink-0">
          {icon}
          {badge != null && badge > 0 && (
            // Dot-not-count on the icon: the MessageCircle icon's left curve
            // was reading visually as a leading "0" next to the count, e.g.
            // "014" instead of "14". The exact count is shown beside the label
            // in labels mode, and on the conversation row's avatar.
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-secondary rounded-full ring-2 ring-surface-2 animate-badge-pulse" aria-label={`${badge} unread`} />
          )}
        </span>
      )}
      {showLabel && (
        <span className="text-sm whitespace-nowrap overflow-hidden flex-1 text-left">
          {label}
        </span>
      )}
      {showLabel && badge != null && badge > 0 && (
        <span className="bg-secondary text-secondary-on text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
